import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * For users who already have an account: accept a company invite while logged in
 * (any active portal). Redirects to customer dashboard after switching role if needed.
 */
const AcceptCustomerInvite = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const navigate = useNavigate();
  const { switchRole } = useAuth();
  const [status, setStatus] = useState('working'); // working | done | error
  const [message, setMessage] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (ranRef.current) return;
      if (!token) {
        setStatus('error');
        setMessage('Missing invitation link. Ask your administrator to resend the invite.');
        return;
      }
      ranRef.current = true;
      try {
        await api.post('/customers/profile/accept-team-invite/', { token });
        if (cancelled) return;
        try {
          await switchRole('customer');
        } catch {
          /* e.g. already on customer portal or token refresh issue */
        }
        setStatus('done');
        navigate('/customer/dashboard', { replace: true });
      } catch (err) {
        if (cancelled) return;
        const d = err.response?.data;
        setStatus('error');
        setMessage(
          typeof d === 'string'
            ? d
            : d?.error || d?.detail || 'Could not accept this invitation.',
        );
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, switchRole]);

  if (status === 'working') {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Invitation
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            {message}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/customer/profile')}>
            Go to profile
          </Button>
        </Paper>
      </Container>
    );
  }

  return null;
};

export default AcceptCustomerInvite;
